import React, { useCallback, useEffect, useMemo } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";

import { extractSubdomainAndBaseAsync } from "~/utils/domainLazy";

import FormLabel from "../../login/formLabel/FormLabel";
import Button from "../../mantine/button/Button";
import Select from "../../mantine/select/Select";
import Input from "../../ui/input/Input";
import Modal from "../../ui/modal/Modal";
import TextArea from "../../ui/textArea/TextArea";
import styles from "../SiteSpeed.module.scss";
import {
  DEFAULT_SUBDOMAIN,
  DEFAULT_TTL,
  dnsPayloadModel,
  dnsRecordObjectTypes,
  ensureTrailingDot,
  normalizeNameToFQDN,
  partialDnsRecordObjectTypes,
  ttlOptions,
} from "../helpers";
import {
  DnsRecordData,
  DnsRecordModel,
  EditDnsRecordModalProps,
  FormData,
  ModalInitialData,
  ValidationRule,
} from "../types";
import {
  caaTagOptions,
  contentDepsByType,
  contentFormatters,
  contentParsers,
  getDnsNames,
  placeholders,
} from "./formHelpers";
import { buildValidationRules, getErrorMessage } from "./validators";

function getDefaultValues(baseDomain?: string): FormData {
  return {
    id: 0,
    type: "A",
    subdomain: DEFAULT_SUBDOMAIN,
    baseDomain: baseDomain || "",
    content: "",
    ttl: DEFAULT_TTL,
    priority: 0,
    weight: 0,
    port: 0,
    flags: 0,
    tag: "issue",
  };
}

// base "name" dependencies (affect FQDN)
const baseNameDeps: (keyof FormData)[] = ["type", "subdomain", "baseDomain"];

// collect list of existing keys for duplicates check
function collectExistingKeys(rows: DnsRecordData[], currentId?: number) {
  return (rows ?? [])
    .filter((r) => r.id !== currentId)
    .map((r) => ({
      type: String(r.type ?? ""),
      nameFQDN: ensureTrailingDot(String(r.nameFQDN ?? "").toLowerCase()),
      content: String(r.content ?? "")
        .trim()
        .replace(/\s+/g, " "),
    }));
}

const EditRecordModal: React.FC<EditDnsRecordModalProps> = ({
  isOpen,
  closeModal,
  handleSubmit: onApply,
  initialData,
  domain,
  allTableRows,
  mode,
}) => {
  const domainName = domain?.name ? ensureTrailingDot(domain.name) : "";
  const integrationType = domain ? domain.integration_type : null;
  const isFullIntegration = domain?.integration_type === "full";

  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
    clearErrors,
    getValues,
    trigger,
    formState: { errors, isSubmitted, isSubmitSuccessful },
  } = useForm<FormData>({
    mode: "onSubmit",
    reValidateMode: "onSubmit", // do not switch to onChange automatically
    defaultValues: getDefaultValues(domainName),
  });

  const type = watch("type");
  const currentTtl = watch("ttl");
  const domainSites = getDnsNames(domain?.sites || []);
  const existing = useMemo(
    () => collectExistingKeys(allTableRows, initialData.id),
    [allTableRows, initialData.id],
  );

  // create a final list of watched fields for the current type
  const watchedNames = useMemo<(keyof FormData)[]>(() => {
    const extra = contentDepsByType[type] ?? ["content"];

    // make uniq guard
    return Array.from(new Set([...baseNameDeps, ...extra]));
  }, [type]);

  const watchedValues = useWatch({ control, name: watchedNames });

  const getFormattedContent = useCallback(() => {
    const values = getValues();
    const format = contentFormatters[type as keyof typeof contentFormatters];
    const result = format ? format(values) : String(values.content ?? "");

    return result.trim().replace(/\s+/g, " ");
    // warning: bind with watchedValues, to recalculate after they change
  }, [type, getValues, watchedValues]);

  const [subdomain, baseDomain] = useWatch({
    control,
    name: ["subdomain", "baseDomain"],
  });
  const fqdn = useMemo(
    () =>
      normalizeNameToFQDN(
        subdomain ?? "@",
        (baseDomain || domainName)!,
      ).toLowerCase(),
    [subdomain, baseDomain, domainName],
  );

  /** Actual duplicate check */
  const isDuplicate = useCallback(() => {
    const formattedContent = getFormattedContent();

    return existing.some(
      (r) =>
        r.type === type &&
        r.nameFQDN === fqdn &&
        r.content === formattedContent,
    );
  }, [existing, type, fqdn, getFormattedContent]);

  const getType = useCallback(() => String(type), [type]);

  const validationRules = useMemo(
    () =>
      buildValidationRules({
        getType,
        integrationType,
        isDuplicate,
      }),
    [getType, integrationType, isDuplicate],
  );

  // Recalculate duplicates ONLY after the first submission.
  // Before submitting — just clear the error so it doesn’t flicker while typing.
  useEffect(() => {
    if (isSubmitted && !isSubmitSuccessful) {
      void trigger("content"); // on change validation only after submitting
    } else {
      clearErrors("content"); // keep the fiend clean before submitting
    }
  }, [watchedValues, isSubmitted, isSubmitSuccessful, trigger, clearErrors]);

  // Prepare initial modal data (parse name → sub/base; parse content for special types)
  const prepareInitialData = useCallback(
    async (data: ModalInitialData): Promise<FormData> => {
      const baseDefault = getDefaultValues(domainName);

      const name = data?.name || domainName;
      let subdomain = DEFAULT_SUBDOMAIN;
      let baseDomain = domainName;

      if (name) {
        try {
          const parsed = await extractSubdomainAndBaseAsync(name);
          subdomain = parsed.subdomain || DEFAULT_SUBDOMAIN;
          baseDomain = parsed.baseDomain
            ? ensureTrailingDot(parsed.baseDomain)
            : domainName;
        } catch {
          // noop — keep defaults for subdomain and baseDomain
        }
      }

      // Parse content for complex record types
      let extra: Partial<FormData> = {};
      const parser = contentParsers[data.type as keyof typeof contentParsers];
      if (parser && data.content) {
        extra = parser(String(data.content));
      }

      return {
        ...baseDefault,
        ...data,
        subdomain,
        baseDomain,
        ...extra,
      };
    },
    [domainName],
  );

  // open → populate
  useEffect(() => {
    if (!isOpen) return;

    (async () => {
      const filled = await prepareInitialData(initialData);
      reset(filled);
      clearErrors();
    })();
  }, [isOpen, initialData, prepareInitialData]);

  // Clear field-level validation errors when type changes
  useEffect(() => {
    clearErrors();
  }, [type, clearErrors]);

  const handleCloseModal = useCallback(() => {
    if (initialData?.action === "add") {
      reset(getDefaultValues(domainName));
    }
    closeModal();
  }, [initialData?.action, reset, closeModal, domainName]);

  const renderInput = useCallback(
    (opts: {
      name: keyof FormData;
      placeholder?: string;
      rules?: ValidationRule;
      style?: React.CSSProperties;
      readOnly?: boolean;
      noErrors?: boolean;
      inputProps?: Record<string, unknown>;
    }) => {
      const {
        name,
        placeholder = "",
        rules,
        style,
        readOnly,
        noErrors = true,
        inputProps = {},
      } = opts;

      if (name === "subdomain") {
        const {
          ref,
          onChange,
          onBlur,
          name: fieldName,
        } = register(name, rules);
        const value = watch(name);

        return isFullIntegration ? (
          <Input
            name={fieldName}
            ref={ref}
            onBlur={onBlur}
            onChange={(e) => {
              const v = e.target.value === "" ? "@" : e.target.value;
              e.target.value = v;
              onChange(e);
            }}
            value={value === "@" ? "" : value}
            placeholder={value === "@" ? "@" : placeholder}
            readOnly={!!readOnly}
            errormessage={
              errors[name]
                ? getErrorMessage(errors[name], `${String(name)} is required`)
                : ""
            }
            style={style}
            {...inputProps}
          />
        ) : (
          <Controller
            name={name}
            control={control}
            rules={rules}
            render={({ field }) => (
              <Select
                readOnly={!!readOnly}
                style={style}
                data={domainSites.map((r) => ({
                  value: String(r ?? "@"),
                  label: String(r ?? "@"),
                }))}
                value={field.value}
                onChange={(v) => field.onChange(v ?? "@")}
                error={
                  errors[name]
                    ? getErrorMessage(
                        errors[name],
                        `${String(name)} is required`,
                      )
                    : undefined
                }
              />
            )}
          />
        );
      }

      return (
        <Input
          {...register(name, rules as unknown as Record<string, unknown>)}
          placeholder={placeholder}
          readOnly={!!readOnly}
          ownProps={{ noErrors }}
          errormessage={
            errors[name]
              ? getErrorMessage(errors[name], `${String(name)} is required`)
              : ""
          }
          style={style}
          {...inputProps}
        />
      );
    },
    [register, errors, watch, control, isFullIntegration, domainSites],
  );

  const renderNumericInput = useCallback(
    (name: keyof FormData, placeholder: string, style?: React.CSSProperties) =>
      renderInput({
        name,
        placeholder,
        rules: validationRules[name],
        inputProps: { type: "number" },
        style,
      }),
    [renderInput, validationRules],
  );

  const fieldWidth: React.CSSProperties = { width: "258px" };

  const renderValueFields = useCallback(() => {
    switch (type) {
      case "TXT":
      case "SPF":
        return (
          <div className={styles.formTextareaSection}>
            <FormLabel label="Value (Text)" />
            <TextArea
              {...register("content", validationRules.content)}
              placeholder="Enter text value"
              ownProps={{
                errorClassName: styles.inlineError,
                errorMessage: errors["content"]
                  ? getErrorMessage(errors["content"])
                  : "",
              }}
            />
          </div>
        );

      case "MX":
        return (
          <>
            <FormLabel label="Value" />
            <div className={styles.multiFieldRow}>
              {renderNumericInput("priority", "Priority", fieldWidth)}
              {renderInput({
                name: "content",
                placeholder: "Set record value",
                rules: validationRules.content,
                style: fieldWidth,
                inputProps: { errorClassName: styles.inlineError },
                noErrors: false,
              })}
            </div>
          </>
        );

      case "SRV":
        return (
          <>
            <div className={styles.multiFieldRow}>
              {/*
                // @ts-expect-error TS2339 non-existing className */}
              <div className={styles.multiFieldColumn}>
                <FormLabel label="Priority" />
                {renderNumericInput("priority", "0")}
              </div>
              {/*
                // @ts-expect-error TS2339 non-existing className */}
              <div className={styles.multiFieldColumn}>
                <FormLabel label="Weight" />
                {renderNumericInput("weight", "1")}
              </div>
              {/*
                // @ts-expect-error TS2339 non-existing className */}
              <div className={styles.multiFieldColumn}>
                <FormLabel label="Port" />
                {renderNumericInput("port", "443")}
              </div>
            </div>
            <div>
              <FormLabel label="Target" />
              {renderInput({
                name: "content",
                placeholder: "service.example.com",
                rules: validationRules.content,
                inputProps: { errorClassName: styles.inlineError },
                noErrors: false,
              })}
            </div>
          </>
        );

      case "CAA":
        return (
          <>
            <div className={styles.multiFieldRow}>
              {/*
                // @ts-expect-error TS2339 non-existing className */}
              <div className={styles.multiFieldColumn}>
                <FormLabel label="Flags" />
                {renderNumericInput("flags", "0")}
              </div>
              {/*
                // @ts-expect-error TS2339 non-existing className */}
              <div className={styles.multiFieldColumn}>
                <FormLabel label="Tag" />
                <Controller
                  name="tag"
                  control={control}
                  rules={validationRules.tag}
                  render={({ field }) => (
                    <Select
                      {...field}
                      data={caaTagOptions}
                      placeholder="Select tag"
                      error={
                        errors.tag
                          ? getErrorMessage(errors.tag, "Tag is required")
                          : undefined
                      }
                    />
                  )}
                />
              </div>
            </div>
            <div>
              <FormLabel label="Value" />
              {renderInput({
                name: "content",
                placeholder: "ca.example.com",
                rules: validationRules.content,
                inputProps: { errorClassName: styles.inlineError },
                noErrors: false,
              })}
            </div>
          </>
        );

      default:
        return (
          <div>
            <FormLabel label="Value" />
            {renderInput({
              name: "content",
              placeholder: placeholders[type] || "example.com",
              rules: validationRules.content,
              inputProps: { errorClassName: styles.inlineError },
              noErrors: false,
            })}
          </div>
        );
    }
  }, [
    type,
    register,
    errors,
    control,
    renderInput,
    renderNumericInput,
    validationRules,
  ]);

  const onSubmit = useCallback(
    (data: FormData) => {
      const action = initialData?.action === "add" ? "add" : "update";
      const formatted = { ...data };
      const formatter =
        contentFormatters[data.type as keyof typeof contentFormatters];
      if (formatter) formatted.content = formatter(data);

      const fullNameFQDN = ensureTrailingDot(
        normalizeNameToFQDN(
          formatted.subdomain,
          formatted.baseDomain || domainName,
        ),
      );

      const changes = new Map<number, DnsRecordModel>();
      const recordId =
        action === "add" ? Date.now() + Math.random() : initialData.id || 0;

      changes.set(
        recordId,
        dnsPayloadModel({
          id: recordId,
          type: formatted.type,
          name: fullNameFQDN,
          content: formatted.content,
          ttl: formatted.ttl,
          action,
        }),
      );

      if (
        data.type === "MX" &&
        action === "update" &&
        data.ttl !== initialData.ttl &&
        allTableRows.length > 0
      ) {
        allTableRows.forEach((record) => {
          if (record.id === recordId) return;

          if (record.type === "MX" && record.ttl !== data.ttl) {
            changes.set(
              record.id,
              dnsPayloadModel({
                id: record.id,
                type: record.type,
                name: record.nameFQDN,
                content: record.content,
                ttl: data.ttl,
                action: "update",
              }),
            );
          }
        });
      }

      onApply?.(changes);

      if (action === "add") reset(getDefaultValues(domainName));
      closeModal();
    },
    [initialData, onApply, closeModal, reset, domainName, allTableRows],
  );

  const modalTitle = useMemo(
    () =>
      initialData?.action === "add" ? "Add DNS Record" : "Edit DNS Record",
    [initialData?.action],
  );

  const modalDescription = useMemo(
    () =>
      initialData?.action === "add"
        ? "Add a new DNS record to define how your domain behaves, like showing your website content and delivering your emails."
        : "Edit the DNS record to modify how your domain behaves.",
    [initialData?.action],
  );

  const shouldShowMxNote = useMemo(
    () => type === "MX" && initialData?.action !== "add",
    [type, initialData?.action],
  );

  const buttonLabel = mode === "create" ? "Add" : "Change";

  return (
    <Modal
      isOpen={isOpen}
      closeModal={handleCloseModal}
      // @ts-expect-error TS2339 non-existing className
      modalLabel={<h3 className={styles.modalTitle}>{modalTitle}</h3>}
      buttonLabel={buttonLabel}
      formId="editDnsRecordForm"
      width="36rem"
    >
      <div className={styles.modalDescription}>{modalDescription}</div>

      <form
        id="editDnsRecordForm"
        // @ts-expect-error TS2339 non-existing className
        className={styles.form}
        onSubmit={handleSubmit(onSubmit)}
      >
        <div className={styles.formSection}>
          {/*
            // @ts-expect-error TS2339 non-existing className */}
          <div className={styles.typeSection}>
            <FormLabel label="Type" />
            <Controller
              name="type"
              control={control}
              rules={validationRules.type}
              render={({ field }) => (
                <Select
                  className={styles.selectType}
                  data={
                    isFullIntegration
                      ? dnsRecordObjectTypes
                      : partialDnsRecordObjectTypes
                  }
                  value={String(field.value)}
                  onChange={(v) => field.onChange(v ?? "")}
                  error={
                    errors.type
                      ? getErrorMessage(errors.type, "Type is required")
                      : undefined
                  }
                />
              )}
            />
          </div>

          {/*
            // @ts-expect-error TS2339 non-existing className */}
          <div className={styles.ttlSection}>
            <FormLabel label="TTL" />
            {shouldShowMxNote && (
              // @ts-expect-error TS2339 non-existing className
              <p className={styles.ttlInfo}>
                Note: Changing TTL for MX records will update all MX records
              </p>
            )}
            <div className={styles.ttlContainer}>
              {renderNumericInput("ttl", "3600", fieldWidth)}
              <div className={styles.ttlButtons}>
                {ttlOptions.map((option) => (
                  <Button
                    key={option.value}
                    type="button"
                    variant={
                      currentTtl === option.value ? "filled" : "outlined"
                    }
                    size="sm"
                    onClick={() => setValue("ttl", option.value)}
                    // @ts-expect-error TS2339 non-existing className
                    className={styles.ttlButton}
                  >
                    {option.label}
                  </Button>
                ))}
              </div>
            </div>
          </div>

          {/*
            // @ts-expect-error TS2339 non-existing className */}
          <div className={styles.nameGroup}>
            <FormLabel label="Name" />
            <div className={styles.nameInputGroup}>
              {renderInput({
                name: "subdomain",
                placeholder: DEFAULT_SUBDOMAIN,
                rules: validationRules.subdomain,
                inputProps: { errorClassName: styles.inlineError },
                style: fieldWidth,
              })}
              {renderInput({
                name: "baseDomain",
                placeholder: domainName,
                rules: validationRules.baseDomain,
                style: fieldWidth,
                readOnly: true,
              })}
            </div>
          </div>
        </div>

        <div className={styles.valueFieldWrapper}>{renderValueFields()}</div>
      </form>
    </Modal>
  );
};

export default EditRecordModal;
